import { appStorage } from './betaStorage'
export type CompletionKind = 'done' | 'with_help' | 'mostly_myself'

export interface SimpleListItem {
  id: string
  text: string
  done: boolean
  completedByUserId?: string
  completedAt?: string
  completionKind?: CompletionKind
}

export interface TaskList {
  id: string
  title: string
  createdByUserId: string
  createdAt: string
  items: SimpleListItem[]
}

export type RoutineStyle = 'checklist' | 'step_by_step' | 'visual' | 'timed'
export type RoutineRepeat = 'daily' | 'weekdays' | 'weekly' | 'selected_days' | 'none'
export type RoutineDay = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'

export interface RoutineStep extends SimpleListItem {
  icon?: string
  time?: string
  instructions?: string
}

export interface Routine {
  id: string
  title: string
  category: string
  style: RoutineStyle
  repeat: RoutineRepeat
  selectedDays: string[]
  createdByUserId: string
  createdAt: string
  lastResetDate?: string
  steps: RoutineStep[]
}

export type ShoppingCategory = 'Groceries' | 'Pharmacy' | 'Household' | 'Personal' | 'Other'

export interface ShoppingItem extends SimpleListItem {
  category: ShoppingCategory
  addedByUserId: string
}

const LISTS_KEY = 'schub.taskLists.v1'
const ROUTINES_KEY = 'schub.routines.v1'
const SHOPPING_KEY = 'schub.shopping.v1'
const DAY_KEYS: RoutineDay[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

function loadArray<T>(key: string): T[] {
  try {
    const raw = appStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveArray<T>(key: string, value: T[]) {
  appStorage.setItem(key, JSON.stringify(value))
}

function localDateKey(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function routineDayKey(date = new Date()): RoutineDay {
  return DAY_KEYS[date.getDay()]
}

export function isRoutineDueOnDate(routine: Routine, date = new Date()) {
  const day = routineDayKey(date)
  if (routine.repeat === 'daily') return true
  if (routine.repeat === 'weekdays') return !['sun', 'sat'].includes(day)
  if (routine.repeat === 'selected_days') return routine.selectedDays.includes(day)
  if (routine.repeat === 'weekly') {
    if (routine.selectedDays.length > 0) return routine.selectedDays.includes(day)
    return routineDayKey(new Date(routine.createdAt)) === day
  }
  return true
}

function resetRecurringRoutineForToday(routine: Routine, date = new Date()): Routine {
  if (routine.repeat === 'none' || !isRoutineDueOnDate(routine, date)) return routine
  const today = localDateKey(date)
  if (routine.lastResetDate === today) return routine
  return {
    ...routine,
    lastResetDate: today,
    steps: routine.steps.map((step) => ({
      ...step,
      done: false,
      completionKind: undefined,
      completedByUserId: undefined,
      completedAt: undefined,
    })),
  }
}

export const loadTaskLists = () => loadArray<TaskList>(LISTS_KEY)
export const saveTaskLists = (lists: TaskList[]) => saveArray(LISTS_KEY, lists)
export function loadRoutines() {
  const saved = loadArray<Routine>(ROUTINES_KEY)
  const next = saved.map((routine) => resetRecurringRoutineForToday(routine))
  if (JSON.stringify(next) !== JSON.stringify(saved)) saveArray(ROUTINES_KEY, next)
  return next
}
export const saveRoutines = (routines: Routine[]) => saveArray(ROUTINES_KEY, routines)

function legacyShoppingItems(): ShoppingItem[] {
  try {
    const raw = appStorage.getItem('schub.state.v2')
    if (!raw) return []
    const parsed = JSON.parse(raw)
    const updates = Array.isArray(parsed?.updates) ? parsed.updates : []
    return updates
      .filter((update: { type?: string; checklist?: { id?: string; text?: string; done?: boolean }[]; authorId?: string }) => update.type === 'shopping')
      .flatMap((update: { checklist?: { id?: string; text?: string; done?: boolean }[]; authorId?: string }, updateIndex: number) =>
        (update.checklist ?? []).map((item, itemIndex) => ({
          id: item.id ?? `legacy-shop-${updateIndex}-${itemIndex}`,
          text: item.text ?? '',
          done: !!item.done,
          category: 'Groceries' as ShoppingCategory,
          addedByUserId: update.authorId ?? 'u-alex',
        })),
      )
      .filter((item: ShoppingItem) => item.text.trim())
  } catch {
    return []
  }
}

export function loadShopping(): ShoppingItem[] {
  const saved = loadArray<ShoppingItem>(SHOPPING_KEY)
  if (saved.length > 0) return saved
  const migrated = legacyShoppingItems()
  if (migrated.length > 0) saveArray(SHOPPING_KEY, migrated)
  return migrated
}

export const saveShopping = (items: ShoppingItem[]) => saveArray(SHOPPING_KEY, items)

export function addRecipeIngredientsToShopping(ingredients: string[], addedByUserId: string) {
  const current = loadShopping()
  const existing = new Set(current.map((item) => item.text.trim().toLocaleLowerCase()))
  const additions = ingredients
    .map((text) => text.trim())
    .filter((text) => text && !existing.has(text.toLocaleLowerCase()))
    .map((text, index): ShoppingItem => ({
      id: `recipe-shopping-${Date.now()}-${index}`,
      text,
      done: false,
      category: 'Groceries',
      addedByUserId,
    }))

  if (additions.length > 0) saveShopping([...current, ...additions])
  return additions.length
}

